import { NavBar } from 'components/NavBar/NavBar'
import { startOfWeek } from 'date-fns'
import { useMemo, useState } from 'react'
import { Animation, Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { convertMinsToHrsMins, formatDateForDisplay, formatNumbersForDisplay } from 'utils/helpers'
import {
    EmployeeActivityList,
    EmployeeActivityRow,
    EmployeeContractDownloadRow,
    EmployeeContractSummaryCard,
    EmployeeContractsColumn,
    EmployeeHeaderAvatar,
    EmployeeHeaderCard,
    EmployeeHeaderIdentity,
    EmployeeHeaderMeta,
    EmployeeHeaderRight,
    EmployeeHeaderTopRow,
    EmployeeInfoField,
    EmployeeInfoGrid,
    EmployeeOverviewGrid,
    EmployeeOverviewMain,
    EmployeeOverviewSide,
    EmployeePointsPill,
    EmployeeRatingInline,
    EmployeeSchedulecContainer,
    EmployeeSectionHeading,
    EmployeeSectionTitle,
    EmployeeSideCard,
    EmployeeStatCard,
    EmployeeStatCardsRow,
    EmployeeTabButton,
    EmployeeTabPanel,
    EmployeeTabsCard,
    EmployeeTaskList,
    EmployeeTaskRow,
    EmployeeTaskStatusBadge,
    EmployeeTodayStatusRow,
} from '../Dashboard.styled'
import { ScheduleWeeklyCard } from 'pages/Shifts/Shifts.styled'
import Avatar from '../../../assets/images/profileAvatar.png'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useParams } from 'react-router-dom'
import { clearSingleDBEmployee, getSingleDBEmployee } from 'features/dashboard/dbEmployeesSlice'
import { ISingleDBEmployee } from 'features/dashboard/types'
import { taskStatusesData } from '../dashboardData'
import useQueryPagination from 'hooks/useQueryPagination'
import { BodyText, OtherText, ParagraphText } from 'styling/Typography'
import { BigStar } from 'assets/icons/BigStar'
import { TrophyIcon } from 'assets/icons/TrophyIcon'
import { DownloadIcon } from 'assets/icons/DownloadIcon'
import { dietaryPreferences } from 'pages/Food/foodData'
import { educationLevelOptions } from 'pages/Employees/employeesData'
import { BeachIcon } from './icons/BeachIcon'
import { CheckIcon } from './icons/CheckIcon'
import { ClockIcon } from './icons/ClockIcon'
import { PinIcon } from './icons/PinIcon'
import { SuitCaseIcon } from './icons/SuitCaseIcon'
import { TruckIcon } from './icons/TruckIcon'
import { SunriseIcon } from 'pages/Shifts/Icons/SunriseIcon'
import { SunsetIcon } from 'pages/Shifts/Icons/SunsetIcon'
import { RotationIcon } from 'pages/Shifts/Icons/RotationIcon'
import { BeachIcon as ShiftBeachIcon } from 'pages/Shifts/Icons/BeachIcon'
import { DrinkIcon } from 'pages/Shifts/Icons/DrinkIcon'
import { HeartIcon } from 'pages/Shifts/Icons/HeartIcon'
import { SunIcon } from 'pages/Shifts/Icons/SunIcon'
import { MinusIcon } from 'pages/Shifts/Icons/MinusIcon'


const TABS = ['Overview', 'Basic Info', 'Welfare', 'Contract', 'Bank Info'] as const
type TabKey = typeof TABS[number]

type TShift = NonNullable<ISingleDBEmployee['shifts']>[number]

const getIcon = (iconName: string, color: string) => {
    switch (iconName) {
        case 'sunrise': return <SunriseIcon stroke={color || '#ff9900'} />
        case 'sunset': return <SunsetIcon stroke={color || '#ff9900'} />
        case 'rotation': return <RotationIcon stroke={color || '#ff9900'} />
        case 'beach': return <ShiftBeachIcon stroke={color || '#ff9900'} />
        case 'drink': return <DrinkIcon stroke={color || '#ff9900'} />
        case 'heart': return <HeartIcon stroke={color || '#ff9900'} />
        case 'sun': return <SunIcon stroke={color || '#ff9900'} />
        default: return <SunriseIcon stroke={color || '#ff9900'} />
    }
}

const formatDateShort = (date: Date): string => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const dayAbbr = days[date.getDay()]
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${dayAbbr} ${day}.${month}.`
}

const Schedule = ({ date, shift }: { date: Date; shift: TShift | null }) => {
    const color = shift?.color ? '#' + shift.color : '#ff9900'

    return (
        <Flex gap='10px' column width='100px' minWidth='100px'>
            <OtherText>{formatDateShort(date)}</OtherText>
            <ScheduleWeeklyCard color={shift ? color : null}>
                <Flex center gap='0.5rem'>
                    {shift?.name ? getIcon(shift.icon, shift.iconColor ? '#' + shift.iconColor : '#fff') : <MinusIcon stroke='#fff' />}
                    <Flex column>
                        <BodyText
                            style={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                color: shift?.textColor ? '#' + shift.textColor : '#fff',
                            }}
                            weight='bold'
                        >
                            {shift?.name || 'Not Set'}
                        </BodyText>
                        {shift && !shift.absence && (
                            <ParagraphText style={{ color: shift?.textColor ? '#' + shift.textColor : '#fff' }}>
                                {convertMinsToHrsMins(shift.shiftStart)}-{convertMinsToHrsMins(shift.minutes, shift.shiftStart)}
                            </ParagraphText>
                        )}
                    </Flex>
                </Flex>
            </ScheduleWeeklyCard>
        </Flex>
    )
}

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
    <EmployeeInfoField>
        <span className='field-label'>{label}</span>
        <span className='field-value'>{value || value === 0 ? value : '-'}</span>
    </EmployeeInfoField>
)

export const DBSingleEmployee = () => {
    const { id } = useParams()
    const { singleEmployee } = useSelector((state: RootState) => state.dbEmployees)
    const [active, setActive] = useState<TabKey>('Overview')
    const params = useMemo(() => ({ userId: id }), [])
    useQueryPagination({ fetchFunctions: [getSingleDBEmployee], cleanUpFunctions: [clearSingleDBEmployee], params })

    const renderSchedule = () => {
        const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
        const shifts = singleEmployee?.shifts || []

        return Array.from({ length: 7 }, (_, i) => {
            const date = new Date(weekStart)
            date.setDate(date.getDate() + i)
            return { date, shift: shifts[i] || null }
        })
    }

    const basicInfo = singleEmployee?.basicInfo
    const welfare = singleEmployee?.welfare
    const contract = singleEmployee?.contract
    const bankInfo = singleEmployee?.bankInfo
    const attendance = singleEmployee?.attendance

    const fullName = singleEmployee?.fullName || ''
    const [firstName, ...restName] = fullName.split(' ')
    const lastName = restName.join(' ')

    const dietary = dietaryPreferences.find((p) => p.value == welfare?.dietaryPreferences)?.label
    const education = educationLevelOptions.find((l) => l.value == basicInfo?.educationLevel)?.label

    const vacationUsed = attendance?.vacationDaysUsed ?? 0
    const vacationTotal = attendance?.totalPossibleVacationDays ?? 24
    const daysOff = attendance?.daysOff ?? 0
    const daysOffTotal = attendance?.totalPossibleDaysOff ?? 24

    const completedTasks = singleEmployee?.totalCompletedTasks ?? 0
    const openTasks = singleEmployee?.lastFiveTasks?.filter((t) => t.taskStatus !== 4).length ?? 0

    const getTaskBadgeStatus = (statusId: number): 'completed' | 'progress' | 'open' => {
        const status = taskStatusesData.find((s) => s.id == statusId)
        const name = status?.name?.toLowerCase() || ''
        if (name.includes('complete')) return 'completed'
        if (name.includes('progress')) return 'progress'
        return 'open'
    }

    const renderOverview = () => (
        <>
            <EmployeeStatCardsRow>
                <EmployeeStatCard>
                    <BeachIcon />
                    <div className='stat-text'>
                        <span className='stat-label'>Vacation Used</span>
                        <span className='stat-value'>
                            {vacationUsed}
                            <span className='stat-value-sub'>/{vacationTotal}</span>
                        </span>
                    </div>
                </EmployeeStatCard>
                <EmployeeStatCard>
                    <SuitCaseIcon color='#00A6FF' />
                    <div className='stat-text'>
                        <span className='stat-label'>Days Off</span>
                        <span className='stat-value'>
                            {daysOff}
                            <span className='stat-value-sub'>/{daysOffTotal}</span>
                        </span>
                    </div>
                </EmployeeStatCard>
                <EmployeeStatCard>
                    <CheckIcon />
                    <div className='stat-text'>
                        <span className='stat-label'>Tasks This Month</span>
                        <span className='stat-value'>{completedTasks}</span>
                    </div>
                </EmployeeStatCard>
                <EmployeeStatCard>
                    <ClockIcon />
                    <div className='stat-text'>
                        <span className='stat-label'>Open Tasks</span>
                        <span className='stat-value'>
                            {openTasks}
                            <span className='stat-value-sub'>/{singleEmployee?.lastFiveTasks?.length ?? 0}</span>
                        </span>
                    </div>
                </EmployeeStatCard>
            </EmployeeStatCardsRow>

            <EmployeeOverviewGrid>
                <EmployeeOverviewMain>
                    <EmployeeContractSummaryCard>
                        <div className='icon-wrap'>
                            <SuitCaseIcon />
                        </div>
                        <div className='rows'>
                            <Field label='Contract' value={contract?.workScheduleModelName || '-'} />
                            <Field label='End Date' value={contract?.endDate ? formatDateForDisplay(contract.endDate) : '-'} />
                            <Field label='Days Remaining' value={'-'} />
                            <Field label='Accommodation' value={'-'} />
                        </div>
                    </EmployeeContractSummaryCard>

                    <EmployeeSideCard>
                        <div className='side-header'>
                            <span className='title'>RECENT ACTIVITY</span>
                        </div>
                        <EmployeeActivityList>
                            {singleEmployee?.lastFiveTasks?.length ? (
                                singleEmployee.lastFiveTasks.slice(0, 4).map((t, i) => {
                                    const status = getTaskBadgeStatus(t.taskStatus)
                                    const Icon = status === 'completed' ? CheckIcon : ClockIcon
                                    return (
                                        <EmployeeActivityRow key={i}>
                                            <Icon />
                                            <span className='activity-text'>{t.taskName}</span>
                                            <span className='activity-time'>-</span>
                                        </EmployeeActivityRow>
                                    )
                                })
                            ) : (
                                <OtherText grey>No recent activity</OtherText>
                            )}
                        </EmployeeActivityList>
                    </EmployeeSideCard>
                </EmployeeOverviewMain>

                <EmployeeOverviewSide>
                    <EmployeeSideCard>
                        <div className='side-header'>
                            <span className='title'>LAST TASKS</span>
                            <span className='view-all'>View all ›</span>
                        </div>
                        <EmployeeTaskList>
                            <EmployeeTaskRow>
                                <span className='task-header'>Name</span>
                                <span className='task-header'>Status</span>
                            </EmployeeTaskRow>
                            {singleEmployee?.lastFiveTasks?.length ? (
                                singleEmployee.lastFiveTasks.slice(0, 4).map((t, i) => (
                                    <EmployeeTaskRow key={i}>
                                        <span className='task-name'>{t.taskName}</span>
                                        <EmployeeTaskStatusBadge status={getTaskBadgeStatus(t.taskStatus)}>
                                            {taskStatusesData.find((s) => s.id == t.taskStatus)?.name || '-'}
                                        </EmployeeTaskStatusBadge>
                                    </EmployeeTaskRow>
                                ))
                            ) : (
                                <OtherText grey>No tasks</OtherText>
                            )}
                        </EmployeeTaskList>
                    </EmployeeSideCard>

                    <EmployeeSideCard>
                        <div className='side-header'>
                            <span className='title'>TODAY&apos;S STATUS</span>
                        </div>
                        <EmployeeTodayStatusRow>
                            <ClockIcon />
                            <div className='info'>
                                <span className='info-label'>Current Shift</span>
                                <span className='info-value'>{attendance?.currentAttendance || '-'}</span>
                            </div>
                        </EmployeeTodayStatusRow>
                        <EmployeeTodayStatusRow>
                            <PinIcon />
                            <div className='info'>
                                <span className='info-label'>Location</span>
                                <span className='info-value'>-</span>
                            </div>
                        </EmployeeTodayStatusRow>
                        <EmployeeTodayStatusRow>
                            <TruckIcon />
                            <div className='info'>
                                <span className='info-label'>Assigned Machine</span>
                                <span className='info-value'>-</span>
                            </div>
                        </EmployeeTodayStatusRow>
                    </EmployeeSideCard>
                </EmployeeOverviewSide>
            </EmployeeOverviewGrid>
        </>
    )

    const renderBasicInfo = () => (
        <EmployeeInfoGrid columns={3}>
            <Field label='First Name' value={firstName} />
            <Field label='Middle and Last Name' value={lastName} />
            <Field label='Access Type' value={'-'} />

            <Field label='Username' value={basicInfo?.username} />
            <Field label='Nationality' value={basicInfo?.nationality} />
            <Field label='Educational Level' value={education} />

            <Field label='Phone Number' value={basicInfo?.phoneNumber} />
            <Field label='Sec. Phone Number' value={basicInfo?.secondaryPhoneNumber} />
            <Field label='Email' value={basicInfo?.email} />

            <Field label='National ID' value={basicInfo?.nationalId} />
            <Field label='Marital Status' value={basicInfo?.maritalStatus} />
            <Field label='Department' value={'-'} />

            <Field label='Dietary Preference' value={dietary} />
            <Field label='Date of Birth' value={basicInfo?.dateOfBirth ? formatDateForDisplay(basicInfo.dateOfBirth) : '-'} />
            <Field label='Gender' value={'-'} />

            <EmployeeSectionHeading>Emergency Contact</EmployeeSectionHeading>

            <Field label='Relative' value={'-'} />
            <Field label='Phone Number' value={basicInfo?.emergencyContact?.phoneNumber} />
            <Field label='First Name' value={basicInfo?.emergencyContact?.name?.split(' ')[0]} />

            <Field label='Middle and Last Name' value={basicInfo?.emergencyContact?.name?.split(' ').slice(1).join(' ')} />
        </EmployeeInfoGrid>
    )

    const renderWelfare = () => (
        <>
            <EmployeeStatCardsRow>
                <EmployeeStatCard>
                    <BeachIcon />
                    <div className='stat-text'>
                        <span className='stat-label'>Vacation Used</span>
                        <span className='stat-value'>
                            {vacationUsed}
                            <span className='stat-value-sub'>/{vacationTotal}</span>
                        </span>
                    </div>
                </EmployeeStatCard>
                <EmployeeStatCard>
                    <SuitCaseIcon color='#00A6FF' />
                    <div className='stat-text'>
                        <span className='stat-label'>Days Off</span>
                        <span className='stat-value'>
                            {daysOff}
                            <span className='stat-value-sub'>/{daysOffTotal}</span>
                        </span>
                    </div>
                </EmployeeStatCard>
            </EmployeeStatCardsRow>

            <EmployeeInfoGrid columns={3}>
                <Field label='Food' value={dietary} />
                <Field label='Food Allowance External' value={formatNumbersForDisplay(welfare?.foodAllowanceOutCompanyAmount)} />
                <Field label='Transport Allowance Internal' value={formatNumbersForDisplay(welfare?.transportationAllowanceInCompanyAmount)} />

                <Field label='Food Allowance Internal' value={formatNumbersForDisplay(welfare?.foodAllowanceInCompanyAmount)} />
                <Field label='Transport Allowance External' value={formatNumbersForDisplay(welfare?.transportationAllowanceOutCompanyAmount)} />
                <Field label='Transport Type' value={'-'} />

                <Field label='Accommodation Allowance Internal' value={'-'} />
                <Field label='Accommodation Allowance External' value={'-'} />
                <Field label='Accommodation Tier' value={'-'} />

                <Field label='Responsibility Allowance Internal' value={'-'} />
                <Field label='Responsibility Allowance External' value={'-'} />
                <Field label='Extra Duty Allowance Internal' value={'-'} />

                <Field label='Extra Duty Allowance External' value={'-'} />
            </EmployeeInfoGrid>
        </>
    )

    const renderContract = () => (
        <EmployeeInfoGrid columns={4}>
            <Field label='Start Date' value={contract?.startDate ? formatDateForDisplay(contract.startDate) : '-'} />
            <Field label='End Date' value={contract?.endDate ? formatDateForDisplay(contract.endDate) : '-'} />
            <Field label='Contract Type' value={'-'} />
            <EmployeeInfoField>
                <span className='field-label'>Contracts</span>
                <EmployeeContractsColumn>
                    <EmployeeContractDownloadRow>
                        <span className='download-name'>-</span>
                        <span className='download-btn'>{DownloadIcon}</span>
                    </EmployeeContractDownloadRow>
                </EmployeeContractsColumn>
            </EmployeeInfoField>

            <Field label='Salary' value={contract?.salary ? formatNumbersForDisplay(contract.salary) : '-'} />
            <Field label='Overtime' value={contract?.overtime ? 'Yes' : 'No'} />
            <Field label='Role' value={singleEmployee?.roleName} />
            <div />

            <Field label='Work Schedule Model' value={contract?.workScheduleModelName} />
            <Field label='Shifts' value={contract?.shifts?.join(', ')} />
        </EmployeeInfoGrid>
    )

    const renderBankInfo = () => (
        <EmployeeInfoGrid columns={3}>
            <Field label='SSNIT' value={bankInfo?.ssnit} />
            <Field label='Account Number' value={bankInfo?.accountNumber} />
            <Field label='Branch Name' value={bankInfo?.branchName} />

            <Field label='Bank Name' value={bankInfo?.bankName} />
        </EmployeeInfoGrid>
    )

    const renderTab = () => {
        switch (active) {
            case 'Overview':
                return renderOverview()
            case 'Basic Info':
                return renderBasicInfo()
            case 'Welfare':
                return renderWelfare()
            case 'Contract':
                return renderContract()
            case 'Bank Info':
                return renderBankInfo()
            default:
                return null
        }
    }

    return (
        <>
            <NavBar />
            <Animation>
                <PageWrapper noFooter>
                    <Flex between center>
                        <H1WithBack title='Employee Details' arrow />
                    </Flex>

                    <EmployeeHeaderCard>
                        <EmployeeHeaderTopRow>
                            <EmployeeHeaderIdentity>
                                <EmployeeHeaderAvatar>
                                    <img src={basicInfo?.profileImageUrl || Avatar} alt='' />
                                </EmployeeHeaderAvatar>
                                <EmployeeHeaderMeta>
                                    <span className='name'>{singleEmployee?.fullName}</span>
                                    <div className='role-row'>
                                        <span className='role'>{singleEmployee?.roleName?.toUpperCase()}</span>
                                        <span className='status'>
                                            <span className='dot' />
                                            {attendance?.currentAttendance || 'Present'}
                                        </span>
                                    </div>
                                </EmployeeHeaderMeta>
                            </EmployeeHeaderIdentity>

                            <EmployeeHeaderRight>
                                <EmployeePointsPill>
                                    <TrophyIcon />
                                    <span className='value'>{formatNumbersForDisplay(singleEmployee?.totalRewardPoints ?? 0)}</span>
                                    <span className='label'>POINTS</span>
                                </EmployeePointsPill>
                                <EmployeeRatingInline>
                                    <BigStar />
                                    <span className='value'>{singleEmployee?.rating ?? '-'}</span>
                                </EmployeeRatingInline>
                            </EmployeeHeaderRight>
                        </EmployeeHeaderTopRow>

                        <EmployeeSchedulecContainer>
                            {renderSchedule().map(({ date, shift }, i) => (
                                <Schedule date={date} shift={shift} key={i} />
                            ))}
                        </EmployeeSchedulecContainer>
                    </EmployeeHeaderCard>

                    <EmployeeTabsCard>
                        {TABS.map((tab) => (
                            <EmployeeTabButton key={tab} active={active === tab} onClick={() => setActive(tab)}>
                                {tab}
                            </EmployeeTabButton>
                        ))}
                    </EmployeeTabsCard>

                    <EmployeeTabPanel>
                        {active !== 'Overview' && <EmployeeSectionTitle>{active.toUpperCase()}</EmployeeSectionTitle>}
                        {renderTab()}
                    </EmployeeTabPanel>
                </PageWrapper>
            </Animation>
        </>
    )
}
